import React from "react";
import useLanguageChange from "../../hooks/useLanguageChange";
import { useDispatch, useSelector } from "react-redux";
import { GLOBAL_MEETING_DATA, PublicVariables } from "../../constants/PublicVariables";
import { goToPlayStore } from "../../shared/utility";
import CenterModal from "../UI/Modal/CenterModal";
import { BLITZZ_HELP_EMAIL, DEVICE_TYPES, TRACK_SUBSCRIBE_FOR } from "../../constants/Constants";
import {
    setAgentVideoSubscriptionFailed,
    setGuestVideoSubscriptionFailed,
    setLocalVideotrackPublicationFailed,
    setVideoNotSupported,
} from "../../reducers/track-failed-reducer";
import { handleEndCallFunc } from "../Twilio/Room/roomFunction";

export const VideoSubscriptionTrackFailedModals = () => {
    const dispatch = useDispatch();
    const localisedValues = useLanguageChange();
    const {
        videoNotSupported,
        forLocalRemoteCheck,
        agentVideoSubscriptionFailed,
        videoFailedUserNumber,
        videoFailedInDeviceType,
        guestVideoSubscriptionFailed,
    } = useSelector((state) => state.trackFailed);

    const handleDownloadApp = () => {
        handleEndCallFunc();
        closeLocalVideoTrackPublicationFailedPopup();
        closeLocalDeviceVideoNotPlayingPopup();
        closeGuestVideoSubscriptionFailedPopup();
        goToPlayStore();
    };

    const closeLocalVideoTrackPublicationFailedPopup = () => {
        dispatch(setLocalVideotrackPublicationFailed(false));
    };

    const closeLocalDeviceVideoNotPlayingPopup = () => {
        dispatch(setVideoNotSupported(false));
    };

    const closeAgentVideoSubscriptionFailedPopup = () => {
        dispatch(setAgentVideoSubscriptionFailed(false));
    };

    const closeGuestVideoSubscriptionFailedPopup = () => {
        dispatch(setGuestVideoSubscriptionFailed(false));
    };

    const videoHeader = (closeModal, headerVal) => {
        return (
            <>
                <h5 className='modal-title'>{headerVal ?? localisedValues?.video_unsupported}</h5>
                <button type='button' className='btn-close' aria-label='Close' onClick={() => closeModal()}>
                    <span className='btn-icon icon-close-light'></span>
                </button>
            </>
        );
    };

    const localDeviceVideoNotPlayingBody = (
        <div className='cms-modal-text'>
            {forLocalRemoteCheck === TRACK_SUBSCRIBE_FOR.REMOTE && (
                <p>
                    <span>{localisedValues?.unable_to_play_video} </span>
                    <span>{localisedValues?.please_install_our_app_to_continue}</span>
                </p>
            )}
            {forLocalRemoteCheck === TRACK_SUBSCRIBE_FOR.LOCAL && (
                <p>
                    <span>{localisedValues?.unable_to_playback_video} </span>
                    <span>{localisedValues?.please_install_our_app_to_continue}</span>
                </p>
            )}
            {GLOBAL_MEETING_DATA.isAgent && (
                <p>
                    <strong>{localisedValues?.note_colon}</strong> <span>{localisedValues?.host_features_will_not_be_available_when_using_app}</span>
                </p>
            )}
        </div>
    );

    const agentVideoSubscriptionFailedBody = (
        <div className='cms-modal-text'>
            <span>{localisedValues?.participant}</span> <span>{videoFailedUserNumber}</span>
            <span>{localisedValues?.not_able_to_see_your_stream}</span>{" "}
            {videoFailedInDeviceType === DEVICE_TYPES.SMART_GLASS && (
                <span>
                    <span>{localisedValues?.video_settings_need_to_be_configured_by_admin_web}</span>
                    <span>
                        <a className='text-dec-none' href={`mailto:${BLITZZ_HELP_EMAIL}`} target='_blank'>
                            {BLITZZ_HELP_EMAIL}
                        </a>
                    </span>
                </span>
            )}
            {videoFailedInDeviceType !== DEVICE_TYPES.SMART_GLASS && <span>{localisedValues?.device_not_supported}</span>}
        </div>
    );

    const guestVideoSubscriptionFailedBody = (
        <div className='cms-modal-text'>
            <p>
                <span>{localisedValues?.there_was_an_error_trying_to_share_your_video}</span>{" "}
                <span>{localisedValues?.please_stop_video_and_start_video_again_to_resume}</span>
                {PublicVariables.isMobile && <span>{localisedValues?.still_facing_issue_connect_mobile_app}</span>}
            </p>
            {GLOBAL_MEETING_DATA.isAgent && (
                <p>
                    <strong>{localisedValues?.note_colon}</strong> <span>{localisedValues?.host_features_will_not_be_available_when_using_app}</span>
                </p>
            )}
        </div>
    );

    const videoFooter = (closeModal, closeBtnVal, actionBtnVal, actionFun, showActionBtn = true) => {
        return (
            <div className='button-inline more-space button-inline-tb-space d-flex align-items-center'>
                <div className='button'>
                    <button className='btn default-btn-link' onClick={() => closeModal()}>
                        {closeBtnVal}
                    </button>
                </div>
                {showActionBtn && (
                    <div className='button'>
                        <button className='btn default-btn-primary' onClick={() => actionFun()}>
                            {actionBtnVal}
                        </button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            {/* video not supported Pop-up - video not playing in local device */}
            <CenterModal
                showModal={videoNotSupported}
                closeModal={() => closeLocalDeviceVideoNotPlayingPopup()}
                modalClass='default-modal modal-small mob-bottom-top'
                bodyClassName='default-custom-scroll'
                className='modal-dialog-centered'
                header={videoHeader(closeLocalDeviceVideoNotPlayingPopup)}
                body={localDeviceVideoNotPlayingBody}
                footer={videoFooter(
                    closeLocalDeviceVideoNotPlayingPopup,
                    localisedValues?.okay_cap,
                    localisedValues?.download_app,
                    handleDownloadApp
                )}
                backdrop={"static"}
            />
            {/* video not supported Pop-up - agent only - when remote user could not play agent's video */}
            <CenterModal
                showModal={agentVideoSubscriptionFailed}
                closeModal={() => closeAgentVideoSubscriptionFailedPopup()}
                modalClass='default-modal modal-small mob-bottom-top'
                bodyClassName='default-custom-scroll'
                className='modal-dialog-centered'
                header={videoHeader(closeAgentVideoSubscriptionFailedPopup)}
                body={agentVideoSubscriptionFailedBody}
                footer={videoFooter(closeAgentVideoSubscriptionFailedPopup, localisedValues?.okay_cap, null, null, false)}
                backdrop={"static"}
            />
            {/* video not supported Pop-up - Guest only - when remote user could not play Guest's video */}
            <CenterModal
                showModal={guestVideoSubscriptionFailed}
                closeModal={() => closeGuestVideoSubscriptionFailedPopup()}
                modalClass='default-modal modal-small mob-bottom-top'
                bodyClassName='default-custom-scroll'
                className='modal-dialog-centered'
                header={videoHeader(closeGuestVideoSubscriptionFailedPopup, localisedValues?.try_again)}
                body={guestVideoSubscriptionFailedBody}
                footer={videoFooter(
                    closeGuestVideoSubscriptionFailedPopup,
                    localisedValues?.okay_cap,
                    localisedValues?.open_in_app,
                    handleDownloadApp,
                    PublicVariables.isMobile
                )}
                backdrop={"static"}
            />
        </>
    );
};

export const VideoTrackPublicationFailedModals = () => {
    const dispatch = useDispatch();
    const localisedValues = useLanguageChange();
    const { localVideoTrackPublicationFailed } = useSelector((state) => state.trackFailed);

    const closePopup = () => {
        dispatch(setLocalVideotrackPublicationFailed(false));
    };

    const handleDownloadApp = () => {
        handleEndCallFunc();
        closePopup();
        goToPlayStore();
    };

    const localVideoTrackPublicationFailedHeader = (
        <>
            <h5 className='modal-title'>{localisedValues?.video_unsupported}</h5>
            <button type='button' className='btn-close' aria-label='Close' onClick={() => closePopup()}>
                <span className='btn-icon icon-close-light'></span>
            </button>
        </>
    );

    const localVideoTrackPublicationFailedBody = (
        <div className='cms-modal-text'>
            <p>
                <span>{localisedValues?.unable_to_start_the_video_please_download_the_app_to_continue}</span>
            </p>
            {GLOBAL_MEETING_DATA.isAgent && (
                <p>
                    <strong>{localisedValues?.note_colon}</strong> <span>{localisedValues?.host_features_will_not_be_available_when_using_app}</span>
                </p>
            )}
        </div>
    );

    const localVideoTrackPublicationFailedFooter = (
        <div className='button-inline more-space button-inline-tb-space d-flex align-items-center'>
            <div className='button'>
                <button className='btn default-btn-link' onClick={() => closePopup()}>
                    {localisedValues?.okay_cap}
                </button>
            </div>
            <div className='button'>
                <button className='btn default-btn-primary' onClick={() => handleDownloadApp()}>
                    {localisedValues?.download_app}
                </button>
            </div>
        </div>
    );

    return (
        <CenterModal
            showModal={localVideoTrackPublicationFailed}
            closeModal={() => closePopup()}
            modalClass='default-modal modal-small mob-bottom-top'
            bodyClassName='default-custom-scroll'
            className='modal-dialog-centered'
            header={localVideoTrackPublicationFailedHeader}
            body={localVideoTrackPublicationFailedBody}
            footer={localVideoTrackPublicationFailedFooter}
            backdrop={"static"}
        />
    );
};
